class SingleSlider {
  #el: HTMLElement;
  #track!: HTMLElement;
  #slides: HTMLElement[] = [];
  #prevBtn: HTMLButtonElement | null = null;
  #nextBtn: HTMLButtonElement | null = null;
  #dotsContainer: HTMLElement | null = null;
  #currentIndex = 0;
  #autoplayTimer: ReturnType<typeof setInterval> | null = null;
  #autoplayMs = 0;

  constructor(el: HTMLElement) {
    this.#el = el;

    const blockId = el.dataset.blockId;
    if (!blockId) return;

    const track = el.querySelector<HTMLElement>(`#SliderTrack-${blockId}`);
    if (!track) return;

    this.#track = track;
    this.#prevBtn = el.querySelector<HTMLButtonElement>(`#SliderPrev-${blockId}`);
    this.#nextBtn = el.querySelector<HTMLButtonElement>(`#SliderNext-${blockId}`);
    this.#dotsContainer = el.querySelector<HTMLElement>(`#SliderDots-${blockId}`);
    this.#autoplayMs = parseInt(el.dataset.autoplay ?? '0', 10);

    this.#init();
  }

  #init(): void {
    this.#slides = Array.from(this.#track.children).filter((child): child is HTMLElement => child instanceof HTMLElement);

    if (this.#slides.length === 0) return;

    for (const slide of this.#slides) {
      slide.classList.add('single-slider__slide');
    }

    this.#bindSlideLinks();
    this.#buildDots();
    this.#bindEvents();
    this.#update();

    if (this.#autoplayMs > 0) {
      this.#startAutoplay();
    }
  }

  #resolveHref(): string | null {
    const fromSetting = this.#el.dataset.link?.trim();
    if (fromSetting) return fromSetting;

    // ponytail: reuse the last slide CTA so existing sliders work without reconfiguration
    for (let i = this.#slides.length - 1; i >= 0; i--) {
      const anchor = this.#slides[i].querySelector<HTMLAnchorElement>('a[href]');
      const href = anchor?.getAttribute('href')?.trim();
      if (href) return href;
    }

    return null;
  }

  #bindSlideLinks(): void {
    const href = this.#resolveHref();
    if (!href) return;

    for (const slide of this.#slides) {
      if (slide.querySelector(':scope > .single-slider__link')) continue;

      slide.classList.add('single-slider__slide--linked');
      const link = document.createElement('a');
      link.className = 'single-slider__link';
      link.href = href;
      link.setAttribute('aria-label', slide.textContent?.trim() || href);
      slide.prepend(link);
    }
  }

  #buildDots(): void {
    if (!this.#dotsContainer) return;

    this.#dotsContainer.innerHTML = '';

    for (let i = 0; i < this.#slides.length; i++) {
      const dot = document.createElement('button');
      dot.className = 'single-slider__dot';
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.dataset.index = String(i);

      dot.addEventListener('click', () => {
        this.#stopAutoplay();
        this.#goTo(i);
      });

      this.#dotsContainer.appendChild(dot);
    }
  }

  #update(): void {
    const count = this.#slides.length;
    const isFirst = this.#currentIndex === 0;
    const isLast = this.#currentIndex === count - 1;

    if (this.#prevBtn) {
      this.#prevBtn.style.display = isFirst ? 'none' : 'flex';
    }

    if (this.#nextBtn) {
      this.#nextBtn.style.display = isLast ? 'none' : 'flex';
    }

    if (!this.#dotsContainer) return;

    const dots = this.#dotsContainer.querySelectorAll<HTMLElement>('.single-slider__dot');

    dots.forEach((dot, i) => {
      dot.classList.toggle('single-slider__dot--active', i === this.#currentIndex);
    });
  }

  #goTo(index: number): void {
    this.#currentIndex = Math.max(0, Math.min(index, this.#slides.length - 1));
    this.#track.scrollTo({ left: this.#track.clientWidth * this.#currentIndex, behavior: 'smooth' });
    this.#update();
  }

  #startAutoplay(): void {
    this.#autoplayTimer = setInterval(() => {
      const next = (this.#currentIndex + 1) % this.#slides.length;
      this.#goTo(next);
    }, this.#autoplayMs);
  }

  #stopAutoplay(): void {
    if (!this.#autoplayTimer) return;
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #bindEvents(): void {
    this.#prevBtn?.addEventListener('click', () => {
      this.#stopAutoplay();
      this.#goTo(this.#currentIndex - 1);
    });

    this.#nextBtn?.addEventListener('click', () => {
      this.#stopAutoplay();
      this.#goTo(this.#currentIndex + 1);
    });

    // Sync index from scroll position (touch swipe / trackpad)
    this.#track.addEventListener(
      'scrollend',
      () => {
        const idx = Math.round(this.#track.scrollLeft / this.#track.clientWidth);

        if (idx !== this.#currentIndex) {
          this.#currentIndex = idx;
          this.#update();
        }
      },
      { passive: true },
    );

    // Fallback for browsers without scrollend
    let scrollTimer: ReturnType<typeof setTimeout>;
    this.#track.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const idx = Math.round(this.#track.scrollLeft / this.#track.clientWidth);

          if (idx !== this.#currentIndex) {
            this.#currentIndex = idx;
            this.#update();
          }
        }, 100);
      },
      { passive: true },
    );

    // Hover pause is a desktop interaction. Opt out with data-pause-on-hover="false".
    if (this.#el.dataset.pauseOnHover !== 'false') {
      this.#el.addEventListener('mouseenter', () => this.#stopAutoplay());
      this.#el.addEventListener('mouseleave', () => {
        if (this.#autoplayMs > 0) this.#startAutoplay();
      });
    }
  }
}

export function initSingleSlider(root: ParentNode = document): void {
  const sliders = Array.from(root.querySelectorAll<HTMLElement>('[data-single-slider]:not([data-slider-init])'));

  for (const el of sliders) {
    el.dataset.sliderInit = 'true';
    new SingleSlider(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSingleSlider());
} else {
  initSingleSlider();
}

document.addEventListener('shopify:section:load', (e: Event) => {
  const target = (e as CustomEvent).target as ParentNode | null;
  initSingleSlider(target ?? document);
});
